import { cellAtWorld, heroWalkable } from '../grid.ts';
import { dist2, idiv, imax, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { CairnRules, EchoRules, FallenRules } from '../aster-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { useSkills } from './ghost.ts';
import { cairnOrder } from '../battle/cairns.ts';
import { echoOrder } from '../battle/echo.ts';
import { fallenOrder } from '../battle/fallen.ts';
import { knotFits } from '../battle/aster-layout.ts';
import { sendaCue } from '../battle/senda.ts';
import { worldToHex } from '../hex.ts';

/**
 * The lab's bot for the ranger's chapter 1 (docs/sendas/explorador.md, «Las piedras de Aster»; the common bot in
 * ai/senda-bot.ts hands these acts here). It only moves the hero, picks its target and taps the marks, as a player's
 * commands would.
 *
 * - the knots (`cairns`): who knows reads each cord (rings, side, the tight knot) and goes to the one cairn it names;
 *   who doesn't goes to the standing cairn nearest the one it read (the obvious: often a ring short), and on.
 * - the fog (`echo`): who knows has Sela shout in each fork, waits for every echo, and goes in by the mouth that
 *   didn't answer; who doesn't goes in by the widest mouth, and the next by its order round the chamber after a wall.
 * - the fallen cairn (`fallen`, the test): both step out of the Roller's line when it curls; who knows reads the long
 *   cord's cairn and the one it names, and stands on the base on both rings; who doesn't reads the long cord and stands
 *   on the base nearest it (a ring short), then on the others by their order.
 *
 * Both shoot what comes near (the ranger's bow), and stand still while reading or settling a base.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** Shoots `t` from where it stands (no walking to it). */
const shoot = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  h.target = t.id;
  h.forced = false;
};

/** What is near enough to shoot without stepping (the nearest). */
const inReach = (s: BattleState, h: Hero, r = h.stats.range - 200): Enemy | null => nearestTo(enemiesNear(s, h.x, h.y, r, (e) => targetable(s, e)), h.x, h.y);

/**
 * How long the bot stands looking at a cord before it goes (counting the rings, finding the side): what a person who
 * knows the rule takes too; without it the bot read and walked faster than anyone thinks.
 */
const THINK = 60;

/* ---------- the knots ---------- */

/** The cairn the bot means to read next (−1: none). */
const cairnNext = (s: BattleState, r: CairnRules, knows: boolean): number => {
  const w = s.senda?.cairns;
  if (!w) return -1;
  if (w.step === 0) return r.way[0] as number;
  const from = r.cairns[w.reading] as CairnRules['cairns'][number];
  const fh = worldToHex(from.x, from.y);
  if (knows) {
    const k = r.knots[w.step - 1];
    if (!k) return -1;
    return r.cairns.findIndex((c, i) => w.st[i] === 0 && knotFits(fh, worldToHex(c.x, c.y), c.plate, k));
  }
  // The nearest standing cairn to the one read.
  let best = -1;
  let bd = 0x7fffffff;
  r.cairns.forEach((c, i) => {
    if (w.st[i] !== 0) return;
    const d = dist2(c.x, c.y, from.x, from.y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
};

export const cairnsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cairns;
  const w = s.senda?.cairns;
  if (!r || !w) return;
  if (w.done >= 0) {
    const t = inReach(s, h);
    if (t) shoot(s, h, t);
    return;
  }
  // Reading: stand and shoot what is in reach.
  if (w.job >= 0) {
    const c = r.cairns[w.job] as CairnRules['cairns'][number];
    if (withinRange(h.x, h.y, c.x, c.y, r.reach) && !h.moving) {
      const t = inReach(s, h);
      if (t) shoot(s, h, t);
      return;
    }
  }
  // Too many on the hero: fight first.
  const near = enemiesNear(s, h.x, h.y, 3200, (e) => targetable(s, e));
  if (near.length >= 3 || (near.length && h.hp * 2 < h.stats.maxHp)) {
    const t = nearestTo(near, h.x, h.y);
    if (t) return shoot(s, h, t);
  }
  // A cord just read: a moment to count its rings.
  if (w.at >= 0 && s.tick < w.at + THINK) {
    const t = inReach(s, h);
    if (t) shoot(s, h, t);
    return;
  }
  const want = cairnNext(s, r, knows);
  if (want < 0) return;
  // (Shooting stops the walk: sent again while it stands short of the cairn.)
  const c = r.cairns[want] as CairnRules['cairns'][number];
  // (Walking, it holds its bow: a target in reach stops the walk.)
  h.target = 0;
  if (w.job !== want || (!h.moving && !withinRange(h.x, h.y, c.x, c.y, r.reach))) cairnOrder(s, h, want);
};

/* ---------- the fog ---------- */

/** The blind bot's mouths tried (by its fork): their order, widest first, the rest round the chamber. */
const blindOrder = (f: EchoRules['forks'][number]): number[] => {
  const idx = f.mouths.map((_, i) => i);
  return [...idx.filter((i) => f.mouths[i]?.wide), ...idx.filter((i) => !f.mouths[i]?.wide)];
};

export const echoBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.echo;
  const w = s.senda?.echo;
  if (!r || !w || w.done >= 0) return;
  const sela = s.senda?.npcs.find((n) => n.id === 'sela' && n.hp > 0);
  // What is on Sela or on the hero first.
  const onSela = sela ? nearestTo(enemiesNear(s, sela.x, sela.y, 2200, (e) => targetable(s, e)), sela.x, sela.y) : null;
  const onMe = inReach(s, h, 2600);
  const threat = onSela ?? onMe;
  if (threat) {
    shoot(s, h, threat);
    // Hold still while they come (Sela catches up).
    if (h.moving && withinRange(h.x, h.y, threat.x, threat.y, 3000)) return;
  }
  if (w.fork >= r.forks.length) return go(s, h, r.exit.x, r.exit.y);
  const f = r.forks[w.fork] as EchoRules['forks'][number];
  const inFork = withinRange(h.x, h.y, f.at.x, f.at.y, r.reach);
  if (w.job >= 0) {
    const m = f.mouths[w.job];
    if (!m) return;
    // A dead end walked to its wall: back to the chamber, the next try.
    if (!m.open && (w.walled[w.fork] ?? 0) & (1 << w.job)) {
      w.job = -1;
      return go(s, h, f.at.x, f.at.y);
    }
    // (Shooting stops the walk: on again.)
    if (!h.moving) go(s, h, m.end.x, m.end.y);
    return;
  }
  if (!inFork) return go(s, h, f.at.x, f.at.y);
  // In the fork, Sela with the hero.
  if (sela && !withinRange(sela.x, sela.y, f.at.x, f.at.y, 1300)) return;
  if (knows) {
    const shoutHere = w.shout.t >= 0 && w.shout.fork === w.fork;
    if (!shoutHere) {
      echoOrder(s, h, 0, (c) => sendaCue(s, c));
      return;
    }
    // Every echo back (the furthest wall's time and a beat).
    if (w.echoes.length || s.tick < w.shout.t + idiv(r.far * 2, r.speed) + 6) return;
    const heard = w.heard[w.fork] ?? 0;
    const m = f.mouths.findIndex((_, i) => !(heard & (1 << i)) && !((w.walled[w.fork] ?? 0) & (1 << i)));
    if (m >= 0) echoOrder(s, h, 1 + m, (c) => sendaCue(s, c));
    return;
  }
  const walled = w.walled[w.fork] ?? 0;
  const m = blindOrder(f).find((i) => !(walled & (1 << i)));
  if (m !== undefined) echoOrder(s, h, 1 + m, (c) => sendaCue(s, c));
};

/* ---------- the fallen cairn ---------- */

/** Out of the Roller's line while it curls or rolls: a step across it. True if it had to. */
const offLine = (s: BattleState, h: Hero, r: FallenRules): boolean => {
  const w = s.senda?.fallen;
  if (!w || !w.roller.id || (w.roller.mode !== 1 && w.roller.mode !== 2)) return false;
  const e = findEnemy(s, w.roller.id);
  if (!e) return false;
  const p = w.roller;
  // Where the hero is along the line and off it.
  const rx = h.x - e.x;
  const ry = h.y - e.y;
  const along = idiv(rx * p.dx + ry * p.dy, 1000);
  const off = idiv(rx * -p.dy + ry * p.dx, 1000);
  if (along < -600 || Math.abs(off) > r.roller.r + 500) return false;
  const side = off >= 0 ? 1 : -1;
  for (const k of [side, -side]) {
    for (const d of [1700, 2400]) {
      const x = h.x + idiv(-p.dy * d * k, 1000);
      const y = h.y + idiv(p.dx * d * k, 1000);
      if (!heroWalkable(s, cellAtWorld(s, x, y))) continue;
      h.target = 0;
      go(s, h, x, y, 150);
      return true;
    }
  }
  return false;
};

/** The base the bot means to stand on next (−1: read first). */
const baseNext = (s: BattleState, r: FallenRules, knows: boolean): number => {
  const w = s.senda?.fallen;
  if (!w) return -1;
  if (knows) return w.read[r.long] && w.read[r.named] ? r.truth : -1;
  if (!w.read[r.long]) return -1;
  if (w.bases[r.obvious] === 0) return r.obvious;
  return w.bases.findIndex((b) => b === 0);
};

export const fallenBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.fallen;
  const w = s.senda?.fallen;
  if (!r || !w || w.done >= 0) return;
  if (offLine(s, h, r)) {
    w.job = -1;
    return;
  }
  const rl = w.roller.id ? findEnemy(s, w.roller.id) : null;
  // Settling a base or reading: stand and shoot.
  const settling = w.on >= 0 && !h.moving;
  const reading = w.job >= 0 && !h.moving && withinRange(h.x, h.y, (r.cairns[w.job] as { x: number }).x, (r.cairns[w.job] as { y: number }).y, r.reach);
  if (settling || reading) {
    const t = inReach(s, h);
    if (t) shoot(s, h, t);
    return;
  }
  // The Roller at the hero's heels: away from it a little, shooting.
  if (rl && rl.hp > 0 && w.roller.mode === 0 && withinRange(h.x, h.y, rl.x, rl.y, 1300)) {
    const dx = h.x - rl.x;
    const dy = h.y - rl.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    const x = h.x + idiv(dx * 2000, d);
    const y = h.y + idiv(dy * 2000, d);
    if (heroWalkable(s, cellAtWorld(s, x, y))) {
      go(s, h, x, y, 200);
      return;
    }
  }
  // A cord just read: a moment to count its rings.
  if (w.at >= 0 && s.tick < w.at + THINK) {
    const t = inReach(s, h);
    if (t) shoot(s, h, t);
    return;
  }
  const base = baseNext(s, r, knows);
  // A crowd on the hero before it knows where to go: fight it first (with a base to stand on, it goes and shoots from there).
  const near = enemiesNear(s, h.x, h.y, 3000, (e) => targetable(s, e) && e.id !== w.roller.id);
  if (base < 0 && near.length >= 4) {
    const t = nearestTo(near, h.x, h.y);
    if (t) return shoot(s, h, t);
  }
  if (base >= 0) {
    const b = r.bases[base] as FallenRules['bases'][number];
    if (!withinRange(h.x, h.y, b.x, b.y, 300) && (!h.moving || Math.abs(h.destX - b.x) + Math.abs(h.destY - b.y) > 300)) fallenOrder(s, h, r.cairns.length + base);
  } else {
    const want = !w.read[r.long] ? r.long : r.named;
    const c = r.cairns[want] as FallenRules['cairns'][number];
    if (w.job !== want || (!h.moving && !withinRange(h.x, h.y, c.x, c.y, r.reach))) fallenOrder(s, h, want);
  }
  // (Walking, it holds its bow: a target in reach stops the walk.)
  h.target = 0;
};
